import { createPopover } from "../popover/popover.js";
//#region src/dom/ui/tooltip/tooltip.ts
/** Map popover reasons to tooltip reasons, filtering out click/outside-click. */
const REASON_MAP = {
	hover: "hover",
	focus: "focus",
	escape: "escape",
	blur: "blur",
	"imperative-action": "imperative-action"
};
/** @internal */
function createTooltip(options) {
	const popoverOpts = {
		transition: options.transition,
		onOpenChange(open, details) {
			const reason = REASON_MAP[details.reason];
			if (!reason) return;
			const group = options.group?.();
			if (open) group?.notifyOpen();
			else group?.notifyClose();
			const tooltipDetails = details.event ? {
				reason,
				event: details.event
			} : { reason };
			options.onOpenChange(open, tooltipDetails);
		},
		closeOnEscape: () => true,
		closeOnOutsideClick: () => false,
		openOnHover: () => true,
		delay: () => {
			const group = options.group?.();
			if (group?.shouldSkipDelay()) return 0;
			return options.delay?.() ?? group?.delay ?? 600;
		},
		closeDelay: () => {
			const group = options.group?.();
			return options.closeDelay?.() ?? group?.closeDelay ?? 0;
		}
	};
	if (options.onOpenChangeComplete) popoverOpts.onOpenChangeComplete = options.onOpenChangeComplete;
	const popover = createPopover(popoverOpts);
	let isPointerDown = false;
	let popupGroup;
	let unsubscribe;
	function isTriggerPopupOpen() {
		return popupGroup?.isOpenFor(popover.triggerElement) ?? false;
	}
	function isSticky() {
		return options.sticky?.() ?? false;
	}
	function syncPopupGroup() {
		const next = options.popupGroup?.();
		if (next === popupGroup) return;
		unsubscribe?.();
		popupGroup = next;
		unsubscribe = popupGroup?.subscribe(() => {
			if (isTriggerPopupOpen() && !isSticky()) popover.close("imperative-action");
		});
	}
	function setTriggerElement(el) {
		popover.setTriggerElement(el);
		syncPopupGroup();
		if (isTriggerPopupOpen() && !isSticky()) popover.close("imperative-action");
	}
	const { onClick: _, ...baseTriggerProps } = popover.triggerProps;
	const triggerProps = {
		...baseTriggerProps,
		onPointerDown() {
			syncPopupGroup();
			isPointerDown = true;
			if (!isSticky()) popover.close("imperative-action");
		},
		onPointerEnter(event) {
			syncPopupGroup();
			if (options.disabled?.()) return;
			if (isTriggerPopupOpen() && !isSticky()) return;
			if (event.pointerType === "touch") return;
			baseTriggerProps.onPointerEnter(event);
		},
		onFocusIn(event) {
			syncPopupGroup();
			if (options.disabled?.()) return;
			if (isTriggerPopupOpen() && !isSticky()) return;
			if (isPointerDown) {
				isPointerDown = false;
				return;
			}
			baseTriggerProps.onFocusIn(event);
		}
	};
	const popupProps = {
		...popover.popupProps,
		onPointerEnter(event) {
			if (options.disableHoverablePopup?.()) return;
			popover.popupProps.onPointerEnter(event);
		}
	};
	return {
		...popover,
		triggerProps,
		popupProps,
		get triggerElement() {
			return popover.triggerElement;
		},
		setTriggerElement,
		open: () => {
			syncPopupGroup();
			if (!isTriggerPopupOpen() || isSticky()) popover.open("hover");
		},
		close: (reason = "hover") => popover.close(reason),
		destroy() {
			unsubscribe?.();
			popover.destroy();
		}
	};
}
//#endregion
export { createTooltip };
